<script setup lang="ts">
import { ref, watch } from "vue";

interface Props {
  block: any;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  update: [block: any];
}>();

const localBlock = ref(JSON.parse(JSON.stringify(props.block)));

// Inicializar dados se não existirem
if (!localBlock.value.data) {
  localBlock.value.data = {
    src: "",
    alt: "Imagem do relatório",
    caption: "",
  };
}

if (!localBlock.value.style) {
  localBlock.value.style = {
    textAlign: "center",
    maxWidth: "100%",
    margin: "20px 0",
    border: "none",
  };
}

watch(
  () => props.block,
  (newBlock) => {
    localBlock.value = JSON.parse(JSON.stringify(newBlock));
  },
  { deep: true }
);

function onChange() {
  emit("update", localBlock.value);
}

function onFileUpload(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      localBlock.value.data.src = e.target?.result as string;
      localBlock.value.data.alt = file.name;
      onChange();
    };
    reader.readAsDataURL(file);
  }
}

function removeImage() {
  localBlock.value.data.src = "";
  localBlock.value.data.alt = "Imagem do relatório";
  onChange();
}
</script>

<template>
  <div class="image-edit">
    <h4>Editar Imagem</h4>

    <div class="form-group">
      <label>URL da Imagem:</label>
      <input
        v-model="localBlock.data.src"
        @input="onChange"
        type="text"
        placeholder="https://exemplo.com/imagem.jpg"
      />
      <small>Cole a URL da imagem ou faça upload</small>
    </div>

    <div class="upload-section" v-if="!localBlock.data.src">
      <label class="upload-label">
        <input
          type="file"
          @change="onFileUpload"
          accept="image/*"
          class="upload-input"
        />
        <div class="upload-area">
          <span>📁</span>
          <p>Clique para fazer upload de uma imagem</p>
        </div>
      </label>
    </div>

    <div class="image-preview" v-if="localBlock.data.src">
      <img
        :src="localBlock.data.src"
        :alt="localBlock.data.alt"
        class="preview"
      />
      <button @click="removeImage" type="button" class="btn-remove-image">
        Remover Imagem
      </button>
    </div>

    <div class="form-group">
      <label>Texto Alternativo:</label>
      <input
        v-model="localBlock.data.alt"
        @input="onChange"
        type="text"
        placeholder="Descrição da imagem"
      />
    </div>

    <div class="form-group">
      <label>Legenda:</label>
      <input
        v-model="localBlock.data.caption"
        @input="onChange"
        type="text"
        placeholder="Legenda da imagem"
      />
    </div>

    <div class="form-group">
      <label>Alinhamento:</label>
      <select v-model="localBlock.style.textAlign" @change="onChange">
        <option value="left">Esquerda</option>
        <option value="center">Centro</option>
        <option value="right">Direita</option>
      </select>
    </div>

    <div class="form-group">
      <label>Largura Máxima:</label>
      <select v-model="localBlock.style.maxWidth" @change="onChange">
        <option value="100%">100%</option>
        <option value="75%">75%</option>
        <option value="50%">50%</option>
        <option value="25%">25%</option>
      </select>
    </div>

    <div class="form-group">
      <label>Margem:</label>
      <select v-model="localBlock.style.margin" @change="onChange">
        <option value="10px 0">Pequena</option>
        <option value="20px 0">Normal</option>
        <option value="30px 0">Grande</option>
        <option value="40px 0">Muito Grande</option>
      </select>
    </div>

    <div class="form-group">
      <label>Borda:</label>
      <select v-model="localBlock.style.border" @change="onChange">
        <option value="none">Sem Borda</option>
        <option value="1px solid #ddd">Borda Leve</option>
        <option value="2px solid #333">Borda Média</option>
        <option value="3px solid #007bff">Borda Destacada</option>
      </select>
    </div>
  </div>
</template>

<style scoped>
.image-edit {
  padding: 10px 0;
}

h4 {
  margin: 0 0 15px 0;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #555;
  font-size: 13px;
}

input[type="text"],
select {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
}

small {
  color: #666;
  font-size: 11px;
  margin-top: 4px;
  display: block;
}

.upload-section {
  margin-bottom: 15px;
}

.upload-label {
  cursor: pointer;
}

.upload-input {
  display: none;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 30px 20px;
  text-align: center;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: #007bff;
  }

  span {
    font-size: 24px;
    display: block;
    margin-bottom: 8px;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
}

.image-preview {
  text-align: center;
  margin-bottom: 15px;
}

.preview {
  max-width: 100%;
  max-height: 150px;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin-bottom: 10px;
}

.btn-remove-image {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: #c82333;
  }
}
</style>
